//From React
import { useLocation, useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
//Misc
import "react-loading-skeleton/dist/skeleton.css";
//Classes
import { AnalysisMain } from "asset-analysis";
//Components
import { AssetProfileMemo } from "../../components/AnalysisPageComponents/Cards/AssetProfileCard";
import { TechnicalIndicatorsCardMemo } from "../../components/AnalysisPageComponents/Cards/TechnicalsIndicatorsCard";
import { FundamentalIndicatorsCardMemo } from "../../components/AnalysisPageComponents/Cards/FundamentalistIndicatorsCard";
import { SummaryOverviewCardMemo } from "../../components/AnalysisPageComponents/Cards/SummaryOverviewCard";
import PrimaryCard from "../../components/AnalysisPageComponents/Cards/PrimaryCard";
import {
  getSelectedTab,
  NavigationBarMemo,
} from "../../components/AnalysisPageComponents/NavigationBar";
import { fullPageFatalError } from "../../components/AnalysisPageComponents/Cards/ErrorsCard";
import { TechnicalGraphicChartCardMemo } from "../../components/AnalysisPageComponents/Cards/PriceGraphicChartCard";
import { FinancialChartCardMemo } from "../../components/AnalysisPageComponents/Cards/FinanceChartsCard";
import { ImportantIndicatorsCardMemo } from "../../components/AnalysisPageComponents/Cards/ImportantIndicatorsCard";
import LoadingAnalysis from "../../components/AnalysisPageComponents/LoadingAnalysis";
import { StockHealthCardMemo } from "../../components/AnalysisPageComponents/Cards/StockHealthCard";
import LoadingBar from "react-top-loading-bar";
import { ReportPostsService } from "../../services/reports/posts/ReportPosts";
import useReport from "../../hooks/useReport";
import { ReportAssetAnalysisOverview } from "../../services/reports/asset-analysis-overview/ReportAssetAnalysisOverview";
import { ToastContainer } from "react-toastify";
import TriggerHighDemandToast from "../../components/shared/HighDemandToast";
import moment from "moment";
import useAnalysis from "../../hooks/useAnalysis";
import { AssetDetails } from "../../@types/Posts";
import { AnalysisReadableResult } from "asset-analysis/lib/@types/outcoming/analysis-readable-result";
import useWindowDimensions from "../../hooks/useWindowDimension";
import { useTour } from "@reactour/tour";
import useGeneralContext from "../../context/GeneralContext";
import useAnalysisTypePreferences, {
  IPreferences,
} from "../../context/AnalysisTypePreferencesContext";
import { useHistory } from "react-router-dom";
import TrialComponent from "../../components/AnalysisPageComponents/Cards/TrialComponent";
import { AssetReport } from "../../@types/Asset";

export default function AnalysisPage() {
  const [assetDetailsData, setAssetDetailsData] = useState<
    AssetDetails | null | false
  >(null);
  const [assetReportData, setAssetReportData] = useState<AssetReport | null>(
    null
  );
  const [analysisResponseData, setAnalysisResponseData] = useState<any>(null);
  const [analysisReadableResults, setAnalysisReadableResults] =
    useState<AnalysisReadableResult | null>(null);
  const [progressBarValue, setProgressBarValue] = useState<number>(0);

  const { key: encodedId } = useParams<{ key: string }>();
  const { nav } = useParams<{ nav: string }>();
  const location = useLocation();
  let pathname = location.pathname.split("/");
  const actualTab = pathname[3];
  const selectedTabNav = getSelectedTab({ actualTab });
  const isLoadingRef = useRef(false);
  const toastTimeoutRef = useRef<any>();
  const lastTriggedToast = useRef(moment().format());
  const { getOne } = useAnalysis();
  const { width } = useWindowDimensions();
  const { setIsOpen, setCurrentStep, setSteps } = useTour();
  const { tourStart, setDisablePrev } = useGeneralContext();
  const { preferences } = useAnalysisTypePreferences();

  const history = useHistory();

  useEffect(() => {
    const analysisSteps = [
      {
        selector: "#analysis-step-1",
        content:
          "Neste cartão, você consegue visualizar informações úteis como o último preço do ativo, o preço alvo que acredito que esse ativo pode chegar no futuro e o quão seguro eu considero manter este ativo em carteira.",
        action: () => {
          setDisablePrev(true);
        },
        actionAfter: () => {
          setDisablePrev(false);
        },
      },
      {
        selector: "#analysis-step-2",
        content:
          "Aqui você pode visualizar a minha opinião de acordo com seu perfil de investidor, você consegue ter um norte e vários insights com a função Natural Language. Você também pode visualizar as impressões que tive apenas clicando no botão de troca.",
        position: "top",
      },
      {
        selector: "#analysis-step-3",
        position: "top",
        content:
          "Eu também analiso profundamente diversas métricas de saúde de uma empresa e dou uma nota de 0 a 10, facilitando sua compreensão e te fornecendo uma visão de águia 🦅",
      },
      {
        selector: "#analysis-step-4",
        content: `Também analiso alguns dados financeiros importantes e te entrego um resumo sobre cada ponto, com indicações simples como "Ruim", "Bom", "Excelente". Te fornecendo um olhar crítico sobre a operação e o setor da empresa.`,
        position: "top",
      },
      {
        selector: "#analysis-step-5",
        content:
          "Aqui você consegue visualizar o gráfico com histórico de preço diário e volume de negociações (no fundo do gráfico). Caso você curta análise técnica, você pode visualizar no cartão ao lado o ouro da análise técnica profissional. 📈",
        position: "top",
      },
      {
        selector: "#analysis-step-6",
        content:
          "Ao final, analiso alguns dados importantes para sua tomada de decisão, como, por exemplo, se o preço está alto, como está o sentimento do mercado sobre o ativo e o que dizem as notícias atuais. 📄",
        position: "top",
      },
      {
        selector: "#analysis-step-7",
        content:
          "Após analisar todos os dados complexos, eu te entrego um resumo dos Fatores de compra e venda desse ativo, também aproveito e te dou minha opinião sincera de acordo com seu perfil de investidor. 🎯",
        position: "top",
      },
      {
        selector: "#analysis-step-8",
        content:
          "Caso você tenha se interessado por este ativo, você pode seguir ele e acompanhar tudo direto do seu feed, você também pode ver uns stories legais! 📷",
        action: () => {
          setDisablePrev(true);
        },
        actionAfter: () => {
          history.push("/ranking/stock/all");
        },
        position: "bottom",
      },
    ];
    const trialStep = [
      {
        selector: "#trial-step",
        content:
          "Ao escolher um plano, você irá desbloquear as análises desta página.",
        action: () => {
          setDisablePrev(true);
        },
        actionAfter: () => {
          history.push("/ranking/stock/all");
        },
        position: "center",
      },
    ];
    const totalRemaining =
      analysisResponseData?.billingState?.totalRemaining ?? 0;
    if (setSteps && tourStart && analysisResponseData) {
      setTimeout(() => {
        // @ts-ignore
        setSteps(!totalRemaining ? trialStep : analysisSteps);
        setCurrentStep(0);
        setIsOpen(true);
      }, 1500);
    }
  }, [
    analysisResponseData,
    setSteps,
    setCurrentStep,
    setIsOpen,
    tourStart,
    history,
    setDisablePrev,
  ]);

  useEffect(() => {
    const targetElement = document.querySelector("#root");
    if (!targetElement) {
      return;
    }

    if (tourStart) {
      document
        .querySelector("#root > .container")
        ?.classList.add("disable-click");
      document
        .querySelector("#root .container")
        ?.classList.add("disable-click");
    }
  }, [tourStart]);

  //Get Analysis response data for use in asset analysis class...
  useEffect(() => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }

    isLoadingRef.current = true;

    determineToastTriggeability();
    setAnalysisResponseData(null);
    setAnalysisReadableResults(null);

    async function fillAnalysisResponseData() {
      const analysisData = await getOne(encodedId, false);

      if (analysisData) {
        setAnalysisResponseData(analysisData);
        setAssetDetailsData(analysisData.assetDetails);
        setAssetReportData(analysisData.report);

        makeAnalysisResponseReadable(
          analysisData,
          analysisData.assetDetails,
          encodedId,
          preferences
        );
      } else {
        setAnalysisResponseData(false);
      }

      setProgressBarValue(100);
      isLoadingRef.current = false;
    }
    fillAnalysisResponseData();

    /**
     * Updates the data every 60 seconds.
     */
    let updateInterval = setInterval(() => {
      (async () => {
        let analysisData = await getOne(encodedId, true);
        if (analysisData && !tourStart) {
          setAssetDetailsData(analysisData.assetDetails);
          setAssetReportData(analysisData.report);
          setAnalysisResponseData(analysisData);
          makeAnalysisResponseReadable(
            analysisData,
            analysisData.assetDetails,
            encodedId,
            preferences
          );
        }
      })();
    }, 40000);

    return () => {
      clearInterval(updateInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encodedId, preferences]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [encodedId]);

  //Make the analysis readable for the components
  function makeAnalysisResponseReadable(
    analysisResponseData: any,
    assetDetailsData: AssetDetails,
    encodedId: string,
    preferences: IPreferences
  ) {
    const requestResults = analysisResponseData?.results;

    if (requestResults && assetDetailsData) {
      let assetType = assetDetailsData.type;

      const analysisModule = new AnalysisMain(requestResults, {
        indications: {
          technical: preferences.technical,
          fundamental: preferences.fundamental,
          extra: preferences.extra,
          historical: true,
          health: assetType === 1 && preferences.health,
        },
        impressions: {
          fundamental: preferences.fundamental,
          historical: true,
          technical: preferences.technical,
          health: assetType === 1 && preferences.health,
        },
        asset: {
          type: assetType,
          encodedId,
        },
      });

      //Do analysis and get the results
      analysisModule.process();
      const readableResults = analysisModule.getReadableResult();
      setAnalysisReadableResults(readableResults);

      //Send posts report
      if (readableResults) {
        const news = requestResults?.analysisResults?.News;

        //eslint-disable-next-line react-hooks/rules-of-hooks
        const { handleReport: handleReportAssetAnalysisOverview } = useReport(
          new ReportAssetAnalysisOverview(encodedId, assetType, requestResults)
        );

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { handleReport: handleReportPosts } = useReport(
          new ReportPostsService(readableResults, news, encodedId)
        );

        handleReportAssetAnalysisOverview(4500);
        handleReportPosts(6500);
      }
    }
  }

  //Determine whether api is on high-load and show a waiting toast
  function determineToastTriggeability() {
    toastTimeoutRef.current = setTimeout(() => {
      //We use this trigged verification to prevent duplicating the same toast
      const lastTriggedDiff = moment().diff(
        lastTriggedToast.current,
        "seconds"
      );
      const isNotDuplicatedToast = lastTriggedDiff > 0;

      if (isLoadingRef.current && isNotDuplicatedToast) {
        lastTriggedToast.current = moment().format();
        TriggerHighDemandToast();
      }
    }, 17000);
  }

  if (
    analysisResponseData !== false &&
    assetDetailsData !== false &&
    analysisResponseData?.responseStatus !== 1
  ) {
    let analysisResultData = analysisResponseData?.results;
    let investorType =
      analysisResultData?.analysisResults.Informations.investorType;
    let billingState = analysisResponseData?.billingState;

    /**
     * We check if the user has selected any specific tab and return the correct content for view.
     */
    let contentView;

    if (billingState && billingState.totalRemaining === 0) {
      contentView = () => <TrialComponent billingState={billingState} />;
    }

    //No one tab has selected (lets render all Cards)
    else if (!selectedTabNav.tabSelected) {
      contentView = () => {
        if (width <= 991) {
          return (
            <div>
              <div className="media media-dashboard-one mg-b-20">
                <div className="media-body mg-t-5">
                  <SummaryOverviewCardMemo
                    indications={
                      analysisReadableResults
                        ? analysisReadableResults.indications
                        : null
                    }
                    investorType={investorType}
                    preferences={preferences}
                    report={assetReportData}
                  />
                </div>

                {preferences.health && (
                  <div className="mg-b-10">
                    <StockHealthCardMemo
                      assetType={
                        assetDetailsData ? assetDetailsData.type : null
                      }
                      analysedReadableData={
                        analysisReadableResults
                          ? analysisReadableResults.indications.health
                          : null
                      }
                      resultRawData={analysisResultData}
                    />
                  </div>
                )}

                <div className="media-right">
                  <PrimaryCard
                    generatedImpressions={
                      analysisReadableResults
                        ? analysisReadableResults.impressions
                        : null
                    }
                    assetId={encodedId}
                    report={assetReportData}
                  />
                </div>
              </div>

              <div
                className="media d-block d-lg-flex mg-b-20"
                style={{ alignItems: "stretch" }}
              >
                <FinancialChartCardMemo
                  analysisData={analysisResultData ? analysisResultData : null}
                  assetType={assetDetailsData ? assetDetailsData.type : null}
                />

                {preferences.fundamental && (
                  <FundamentalIndicatorsCardMemo
                    analysisData={
                      analysisReadableResults
                        ? analysisReadableResults.indications.fundamental
                        : null
                    }
                    assetType={assetDetailsData ? assetDetailsData.type : null}
                  />
                )}
              </div>

              <div
                className="media d-block d-lg-flex mg-b-20"
                style={{ alignItems: "stretch" }}
              >
                <TechnicalGraphicChartCardMemo
                  analysisData={analysisResultData ? analysisResultData : null}
                />

                {preferences.technical && (
                  <TechnicalIndicatorsCardMemo
                    analysisData={
                      analysisReadableResults
                        ? analysisReadableResults.indications.technical
                        : null
                    }
                  />
                )}
              </div>

              {preferences.extra && (
                <div className="mg-b-20">
                  <ImportantIndicatorsCardMemo
                    movingAverageData={
                      analysisReadableResults
                        ? analysisReadableResults.indications.movingAverage
                        : null
                    }
                    extraData={
                      analysisReadableResults
                        ? analysisReadableResults.indications.extra
                        : null
                    }
                    report={assetReportData}
                  />
                </div>
              )}
            </div>
          );
        } else {
          return (
            <div>
              <div className="media media-dashboard-one mg-b-20">
                <div className="media-body">
                  <PrimaryCard
                    generatedImpressions={
                      analysisReadableResults
                        ? analysisReadableResults.impressions
                        : null
                    }
                    assetId={encodedId}
                    report={assetReportData}
                  />
                </div>

                <div className="media-right">
                  <SummaryOverviewCardMemo
                    indications={
                      analysisReadableResults
                        ? analysisReadableResults.indications
                        : null
                    }
                    investorType={investorType}
                    preferences={preferences}
                    report={assetReportData}
                  />
                </div>
              </div>

              {preferences.health && (
                <div className="mg-b-20">
                  <StockHealthCardMemo
                    assetType={assetDetailsData ? assetDetailsData.type : null}
                    analysedReadableData={
                      analysisReadableResults
                        ? analysisReadableResults.indications.health
                        : null
                    }
                    resultRawData={analysisResultData}
                  />
                </div>
              )}

              {assetDetailsData?.type === 1 && (
                <div
                  className={
                    "media d-block d-lg-flex mg-b-20" +
                    (!preferences.fundamental ? " hide-analysis" : "")
                  }
                  style={{ alignItems: "stretch" }}
                >
                  <FinancialChartCardMemo
                    analysisData={
                      analysisResultData ? analysisResultData : null
                    }
                    assetType={assetDetailsData ? assetDetailsData.type : null}
                  />

                  {preferences.fundamental && (
                    <FundamentalIndicatorsCardMemo
                      analysisData={
                        analysisReadableResults
                          ? analysisReadableResults.indications.fundamental
                          : null
                      }
                      assetType={
                        assetDetailsData ? assetDetailsData.type : null
                      }
                    />
                  )}
                </div>
              )}

              <div
                className={
                  "media d-block d-lg-flex mg-b-20" +
                  (!preferences.technical ? " hide-analysis" : "")
                }
                style={{ alignItems: "stretch" }}
              >
                <TechnicalGraphicChartCardMemo
                  analysisData={analysisResultData ? analysisResultData : null}
                />

                {preferences.technical && (
                  <TechnicalIndicatorsCardMemo
                    analysisData={
                      analysisReadableResults
                        ? analysisReadableResults.indications.technical
                        : null
                    }
                  />
                )}
              </div>

              <div className="mg-b-20">
                {preferences.extra && (
                  <ImportantIndicatorsCardMemo
                    movingAverageData={
                      analysisReadableResults
                        ? analysisReadableResults.indications.movingAverage
                        : null
                    }
                    extraData={
                      analysisReadableResults
                        ? analysisReadableResults.indications.extra
                        : null
                    }
                    report={assetReportData}
                  />
                )}
              </div>
            </div>
          );
        }
      };
    } else {
      // The user selected an specific tab
      contentView = () => {
        if (selectedTabNav.technical) {
          return (
            <div
              className="media d-block d-lg-flex mg-b-20"
              style={{ alignItems: "stretch" }}
            >
              <TechnicalGraphicChartCardMemo
                analysisData={analysisResultData ? analysisResultData : null}
              />
              <TechnicalIndicatorsCardMemo
                analysisData={
                  analysisReadableResults
                    ? analysisReadableResults.indications.technical
                    : null
                }
              />
            </div>
          );
        } else if (selectedTabNav.fundamental) {
          return (
            <div
              className="media d-block d-lg-flex mg-b-20"
              style={{ alignItems: "stretch" }}
            >
              <FinancialChartCardMemo
                analysisData={analysisResultData ? analysisResultData : null}
                assetType={assetDetailsData ? assetDetailsData.type : null}
              />

              <FundamentalIndicatorsCardMemo
                analysisData={
                  analysisReadableResults
                    ? analysisReadableResults.indications.fundamental
                    : null
                }
                assetType={assetDetailsData ? assetDetailsData.type : null}
              />
            </div>
          );
        } else if (selectedTabNav.extra) {
          return (
            <div className="mg-b-20">
              <ImportantIndicatorsCardMemo
                movingAverageData={
                  analysisReadableResults
                    ? analysisReadableResults.indications.movingAverage
                    : null
                }
                extraData={
                  analysisReadableResults
                    ? analysisReadableResults.indications.extra
                    : null
                }
                report={assetReportData}
              />
            </div>
          );
        } else if (selectedTabNav.health) {
          return (
            <div className="mg-b-20">
              <StockHealthCardMemo
                analysedReadableData={
                  analysisReadableResults
                    ? analysisReadableResults.indications.health
                    : null
                }
                assetType={assetDetailsData ? assetDetailsData.type : null}
                resultRawData={analysisResultData}
              />
            </div>
          );
        }
      };
    }

    return (
      <div className="az-content pd-t-20">
        <LoadingBar
          color={"#596EFD"}
          height={3}
          shadow={true}
          loaderSpeed={3000}
          progress={progressBarValue}
        />
        <ToastContainer newestOnTop={true} />
        <div className="az-content-body">
          <LoadingAnalysis
            assetType={assetDetailsData?.type}
            show={!analysisResponseData}
          />
          <AssetProfileMemo
            assetProfileData={assetDetailsData ? assetDetailsData : null}
            analysisData={
              analysisResponseData ? analysisResponseData.results : null
            }
            show={!!analysisResponseData}
            billingState={
              analysisResponseData ? analysisResponseData.billingState : null
            }
          />
          {billingState?.totalRemaining !== 0 && (
            <div className="pd-7">
              <NavigationBarMemo
                actualNav={nav}
                assetId={encodedId}
                preferences={preferences}
              />
            </div>
          )}
          {contentView()}
        </div>
      </div>
    );
    //Skeleton loading below.
  } else {
    return fullPageFatalError();
  }
}
